import React, { useEffect, useMemo, useState } from "react";

const gateApiUrl = "https://www.fhbrundle.co.uk/apitest?cmd=gate&get_gate=";
const gateConfiguratorUrl = "https://www.fhbrundle.co.uk/gate-config?code=";
const gateImageBaseUrl =
  "https://samsara-web.s3-eu-west-1.amazonaws.com/selectors/gate/images/";

const formatMoney = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};

const getNotepadCode = (notepad) =>
  String(notepad || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line && line.toUpperCase() !== "NSGATE") || "";

export const getCustomGateReference = (item = {}) => {
  const explicitReference = String(item.NS_ref || "").trim();
  if (explicitReference) return explicitReference;

  const productMatch = String(item.product || "").match(
    /^NS\s+CUSTOM\s+GATE\s+(.+)$/i
  );
  return productMatch ? productMatch[1].trim() : "";
};

export const isCustomGateItem = (item = {}) =>
  String(item.NS_type || "").toUpperCase() === "GATE" ||
  /^NS\s+CUSTOM\s+GATE\s+/i.test(String(item.product || ""));

const GatePlaceholder = () => (
  <svg
    className="basket-gate-placeholder"
    viewBox="0 0 120 92"
    role="img"
    aria-label="Custom gate"
  >
    <path d="M13 82V10M107 82V10M20 77V20L60 37V77M100 77V20L60 37" />
    <path d="M20 28L60 44L100 28M20 39L60 55L100 39M20 50L60 66L100 50" />
    <circle cx="55" cy="57" r="2.5" />
    <circle cx="65" cy="57" r="2.5" />
  </svg>
);

const BasketPrice = ({ original, discounted, showDiscount }) => (
  <>
    {showDiscount && discounted !== null && discounted !== undefined ? (
      <>
        <span className="basket-price-original">£{formatMoney(original)}</span>
        <strong className="basket-price-discount">£{formatMoney(discounted)}</strong>
      </>
    ) : (
      <>£{formatMoney(original)}</>
    )}
  </>
);

export default function BasketCustomGate({ item }) {
  const gateReference = useMemo(() => getCustomGateReference(item), [item]);
  const fallbackCode = getNotepadCode(item.notepad);
  const [gate, setGate] = useState(null);
  const [status, setStatus] = useState("loading");
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    if (!gateReference) {
      setStatus("error");
      return undefined;
    }

    let active = true;
    setStatus("loading");
    setImageFailed(false);

    fetch(`${gateApiUrl}${encodeURIComponent(gateReference)}`, {
      cache: "no-store",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Gate lookup failed");
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        setGate(data);
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });

    return () => {
      active = false;
    };
  }, [gateReference]);

  const code = gate?.code || fallbackCode || gateReference;
  const websiteUrl = `${gateConfiguratorUrl}${encodeURIComponent(code)}`;
  const imageUrl = gate?.image
    ? `${gateImageBaseUrl}${encodeURIComponent(gate.image)}`
    : "";
  const extras = Array.isArray(gate?.extras) ? gate.extras : [];
  const showUnitDiscount = Boolean(
    item.discount_applies && item.discounted_unit_price
  );
  const showLineDiscount = Boolean(
    item.discount_applies && item.discounted_line_total
  );

  return (
    <article className="product_table prodlayoutdefault basket-gate-card">
      <div className="product_card_img_container basket-gate-preview">
        {imageUrl && !imageFailed ? (
          <img
            className="product_card_img basket-gate-preview__image"
            src={imageUrl}
            alt={`Custom gate ${code}`}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <GatePlaceholder />
        )}
      </div>

      <div className="product_card_content">
        <div className="basket-gate-heading">
          <div>
            <span className="basket-panel-eyebrow">Made to measure</span>
            <h3>Custom gate</h3>
            <p>{code}</p>
          </div>
          <span className="basket-gate-reference">{gateReference}</span>
        </div>

        <div className="basket-gate-details">
          {status === "loading" && (
            <div className="basket-gate-status" role="status">
              <span className="basket-loading__spinner" aria-hidden="true" />
              Loading gate configuration…
            </div>
          )}

          {status === "ready" && (
            <>
              <dl className="basket-gate-specs">
                <div>
                  <dt>Width</dt>
                  <dd>{gate.values?.gate_width || "—"} mm</dd>
                </div>
                <div>
                  <dt>Height</dt>
                  <dd>{gate.values?.gate_height || "—"} mm</dd>
                </div>
                <div>
                  <dt>Gate reference</dt>
                  <dd>{gateReference}</dd>
                </div>
              </dl>

              {extras.length > 0 && (
                <div className="basket-gate-extras">
                  <strong>Extras</strong>
                  {extras.map((extra, index) => (
                    <div key={`${extra.prod_code || "extra"}-${index}`}>
                      <a
                        href={`https://www.fhbrundle.co.uk/products/${encodeURIComponent(
                          extra.prod_code || ""
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {extra.description || extra.prod_code}
                      </a>
                      <span>{extra.prod_code}</span>
                      <b>£{formatMoney(extra.price)}</b>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {status === "error" && (
            <p className="basket-gate-status">
              Gate details are available on the F.H. Brundle website.
            </p>
          )}

          <a
            className="basket-gate-view"
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View gate on website <span aria-hidden="true">↗</span>
          </a>
        </div>

        <div className="product_card_bottom">
          <div className="prod_price">
            <div className="quote_item">
              <div className="quote_label">Quantity</div>
              <div className="quote_value">x{item.quantity}</div>
            </div>
            <div className="quote_item">
              <div className="quote_label">Unit price</div>
              <div className="quote_value">
                <BasketPrice
                  original={item.unit_price}
                  discounted={item.discounted_unit_price}
                  showDiscount={showUnitDiscount}
                />
              </div>
            </div>
            <div className="quote_item">
              <div className="quote_label">Line total</div>
              <div className="quote_value">
                <BasketPrice
                  original={item.line_price}
                  discounted={item.discounted_line_total}
                  showDiscount={showLineDiscount}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
